import classNames from "classnames";
import React, { HTMLAttributes } from "react";

import { AlertClose, AlertIcon, AlertText, StyledAlert, typeColors } from "./Alert.styled";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export type AlertProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Set alert color
   * @type {"blue" | "green" | "grey" | "red" | "yellow"}
   * @default {"red"}
   */
  color?: keyof typeof typeColors;

  /**
   * Set alert icon
   */
  icon?: IconProp;

  /**
   * Set emoticon (an emoticon character)
   */
  emoticon?: string | null;

  /**
   * Set alert with solid border color
   * @default {false}
   */
  bordered?: boolean;

  /**
   * Set alert with close button
   * @default {false}
   */
  withCloseButton?: boolean;

  /**
   * Callback after close button has been clicked
   * @default {false}
   */
  onCloseClick?: () => void;
};

const defaultProps: AlertProps = {
  color: "red",
  icon: null,
  emoticon: null,
  bordered: false,
  withCloseButton: false,
  onCloseClick: () => {}
};

const Alert = React.forwardRef<HTMLDivElement, AlertProps>((props, ref) => {
  const { children, className, color, icon, emoticon, bordered, withCloseButton, onCloseClick, ...restProps } = props;
  const alertClass = classNames("c-alert", className);

  return (
    <StyledAlert
      data-testid="qa-alert"
      ref={ref}
      className={alertClass}
      color={color}
      bordered={bordered}
      {...restProps}
    >
      {icon || emoticon ? (
        <>
          <AlertIcon className="c-alert__icon-wrapper" data-testid="qa-alert-icon-wrapper">
            {icon && <FontAwesomeIcon data-testid="qa-alert-icon" icon={icon} />}
            {emoticon}
          </AlertIcon>
          <AlertText className="c-alert__icon-text" data-testid="qa-alert-icon-text">
            {children}
          </AlertText>
        </>
      ) : (
        <div data-testid="qa-alert-text" className="u-w-full">
          {children}
        </div>
      )}
      {withCloseButton && (
        <AlertClose data-testid="qa-alert-close" color={color} onClick={onCloseClick}>
          <FontAwesomeIcon data-testid="qa-alert-close-icon" icon={faTimes} />
        </AlertClose>
      )}
    </StyledAlert>
  );
});

Alert.defaultProps = defaultProps;

export default Alert;
